/**
 * Polling hook that fires `tick()` every `intervalMs` while the tab is
 * visible, and backs off to 60 s when the tab is hidden. Always fires
 * once on mount so the first paint isn't empty.
 *
 * Used by the dashboard Chats card and the in-modal chat poll.
 */
import { useEffect, useRef } from 'react';

export function usePollWhileVisible(
  tick: () => void | Promise<void>,
  intervalMs: number,
  deps: React.DependencyList = []
): void {
  // Keep the latest callback in a ref so we don't re-create the timer
  // when the consumer recreates the closure on every render.
  const cbRef = useRef(tick);
  useEffect(() => {
    cbRef.current = tick;
  });

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const schedule = () => {
      if (cancelled) return;
      const ms = document.hidden ? 60_000 : intervalMs;
      timer = setTimeout(async () => {
        try {
          await cbRef.current();
        } catch {
          // Swallow — caller is responsible for error UI. Timer keeps going.
        } finally {
          schedule();
        }
      }, ms);
    };

    // Fire immediately on mount + reschedule.
    (async () => {
      try {
        await cbRef.current();
      } catch {
        /* ignore */
      } finally {
        schedule();
      }
    })();

    const onVisibility = () => {
      // When the tab regains focus, fire a fresh tick right away so the
      // user doesn't see stale state during the first second.
      if (!document.hidden) {
        if (timer) clearTimeout(timer);
        (async () => {
          try {
            await cbRef.current();
          } catch {
            /* ignore */
          } finally {
            schedule();
          }
        })();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, ...deps]);
}
